import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { parseLocalDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Photo {
  id: number
  title: string | null
  document_date: string | null
  width: number | null
  height: number | null
  significant: number | null
  notes: string | null
  user_name: string | null
  report_label: string | null
  date_added: string | null
  image_url: string | null
  download_url: string | null
}

interface Props {
  case_record: CaseRecord
  photo: Photo
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = parseLocalDate(dateStr)
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
}

export default function Show({ case_record, photo }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Photos", href: `/cases/${case_record.id}/photos` },
    {
      title: `Photo #${photo.id}`,
      href: `/cases/${case_record.id}/photos/${photo.id}`,
    },
  ]

  const basePath = `/cases/${case_record.id}/photos`

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this photo?")) {
      router.delete(`${basePath}/${photo.id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Photo #${photo.id} - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-center text-2xl font-semibold">
          Photo #{photo.id}
        </h1>

        <div className="mb-4 text-center">
          {photo.image_url ? (
            <img
              src={photo.image_url}
              alt={photo.title ?? `Photo #${photo.id}`}
              className="mx-auto max-h-[600px] rounded"
            />
          ) : (
            <div className="bg-hpi-pale-gray text-hpi-med-gray mx-auto flex h-64 w-64 items-center justify-center rounded">
              No preview
            </div>
          )}
        </div>

        {photo.title && (
          <div className="mb-2 text-center font-medium">{photo.title}</div>
        )}

        {photo.width && photo.height && (
          <div className="text-hpi-med-gray mb-1 text-center text-sm">
            Size: {photo.width}x{photo.height}
          </div>
        )}

        {photo.date_added && (
          <div className="text-hpi-med-gray mb-1 text-center text-sm">
            Added on {formatDate(photo.date_added)}
            {photo.user_name && ` by ${photo.user_name}`}
          </div>
        )}

        {photo.report_label && (
          <div className="text-hpi-med-gray mb-1 text-center text-sm">
            Report: {photo.report_label}
          </div>
        )}

        {photo.notes && (
          <div className="text-hpi-med-gray mb-1 text-center text-sm">
            Notes: {photo.notes}
          </div>
        )}

        {photo.significant === 1 && (
          <div className="text-hpi-green mb-1 text-center text-sm">
            Final Report
          </div>
        )}

        <div className="mt-4 flex justify-center gap-4">
          <Link
            href={`${basePath}/${photo.id}/edit`}
            className="text-hpi-active-blue text-sm hover:underline"
          >
            Edit
          </Link>
          <button
            onClick={handleDelete}
            className="text-hpi-red text-sm hover:underline"
          >
            Delete
          </button>
        </div>

        <div className="mt-6 text-center">
          <Link
            href={basePath}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Return to Photos
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
